import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Panel } from "@/components/common";
import { FieldGrid } from "@/components/Field";
import UsersPanel from "@/components/UsersPanel";

const business = [
  { key: "business_name", label: "Business Name", span: 2 },
  { key: "owner_name", label: "Proprietor" },
  { key: "mobile", label: "Mobile", type: "tel" },
  { key: "email", label: "Email", type: "email" },
  { key: "website", label: "Website" },
  { key: "city", label: "City" },
  { key: "address", label: "Address", type: "textarea", span: 3, rows: 2 },
  { key: "state", label: "State" },
  { key: "state_code", label: "State Code" },
  { key: "gstin", label: "GSTIN" },
  { key: "pan", label: "PAN" },
];
const bank = [
  { key: "bank_name", label: "Bank Name" },
  { key: "account_name", label: "Account Name" },
  { key: "account_number", label: "Account Number" },
  { key: "ifsc", label: "IFSC" },
  { key: "upi_id", label: "UPI ID" },
];
const docs = [
  { key: "invoice_prefix", label: "Invoice Prefix" },
  { key: "quotation_prefix", label: "Quotation Prefix" },
  { key: "default_gst_rate", label: "Default GST %", type: "number" },
  { key: "sac_code", label: "SAC Code" },
  { key: "invoice_terms", label: "Invoice Terms & Conditions", type: "textarea", span: 3, rows: 3 },
];

export default function Settings() {
  const [s, setS] = useState(null);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  const change = (k, v) => setS((x) => ({ ...x, [k]: v }));

  const save = async () => {
    try { const r = await api.put("/settings", { ...s, default_gst_rate: parseFloat(s.default_gst_rate) || 0 }); setS(r.data); toast.success("Business profile saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const changePw = async () => {
    try { await api.post("/auth/change-password", pw); toast.success("Password changed"); setPw({ current_password: "", new_password: "" }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!s) return null;

  return (
    <div data-testid="settings-page">
      <PageHeader eyebrow="System" title="Settings" subtitle="Business profile shown on invoices and quotations."
        actions={<Button onClick={save} className="h-11 sm:h-10" data-testid="settings-save-btn"><Save className="h-4 w-4 mr-1.5" /> Save Profile</Button>} />
      <div className="space-y-6 max-w-5xl">
        <Panel title="Business Profile"><FieldGrid fields={business} data={s} onChange={change} testPrefix="settings" /></Panel>
        <Panel title="Bank Details (printed on invoice)"><FieldGrid fields={bank} data={s} onChange={change} testPrefix="settings" /></Panel>
        <Panel title="Document Settings"><FieldGrid fields={docs} data={s} onChange={change} cols={4} testPrefix="settings" /></Panel>
        <UsersPanel />
        <Panel title="Change Password">
          <FieldGrid cols={3} testPrefix="pw" data={pw} onChange={(k, v) => setPw((p) => ({ ...p, [k]: v }))}
            fields={[{ key: "current_password", label: "Current Password", type: "password" }, { key: "new_password", label: "New Password (min 8)", type: "password" }]} />
          <Button variant="outline" className="mt-4" onClick={changePw} disabled={!pw.current_password || !pw.new_password} data-testid="change-password-btn">
            <KeyRound className="h-4 w-4 mr-1.5" /> Update Password
          </Button>
        </Panel>
      </div>
    </div>
  );
}
